import { Select, TextInput } from '@mantine/core';
import useWhoAmI from '@/hooks/useWhoAmI';
import { useForm } from '@mantine/form';
import EditUserFormBase from './EditUserFormBase';
import { genders, pronouns } from '@/lib/data';
import {
  GenderIdentityCode,
  PronounCode,
  UpdatePatientInput,
} from '@/lib/graphql/types';
import { useEffect } from 'react';

type CodesPatientInput = Pick<
  UpdatePatientInput,
  'genderIdentity' | 'pronouns'
>;

interface EditFormWithCodeProps {
  label: string;
  valueName: keyof UpdatePatientInput;
  onClose: () => void;
}

const SELECT_DATA_SET = {
  genderIdentity: genders,
  pronouns: pronouns,
};

const EditFormWithCode = ({
  label,
  valueName,
  onClose,
}: EditFormWithCodeProps) => {
  const { user, isLoading } = useWhoAmI();

  const codesPatientValueName = valueName as keyof CodesPatientInput;

  const { text, value } = user?.[codesPatientValueName] ?? {};

  const form = useForm<CodesPatientInput>({
    initialValues: {
      [valueName]: {
        text,
        value,
      },
    },
    validate: {
      [valueName]: {
        text: (value: string, values: CodesPatientInput) =>
          values[codesPatientValueName]?.value === 'OTHER' && value.length < 1
            ? 'Please enter your gender'
            : null,
      },
    },
    validateInputOnBlur: true,
  });

  useEffect(() => {
    if (
      !isLoading &&
      !form.values[codesPatientValueName]?.value &&
      user &&
      user[codesPatientValueName]?.value
    ) {
      form.setValues({
        [valueName]: { ...user[codesPatientValueName] },
      });
    }
  }, [user, isLoading, form, valueName, codesPatientValueName]);

  return (
    <EditUserFormBase form={form} onClose={onClose}>
      <Select
        name={label}
        placeholder={label}
        data={SELECT_DATA_SET[codesPatientValueName]}
        {...form.getInputProps(`${codesPatientValueName}.value`)}
      />
      {(form.values[codesPatientValueName]?.value ===
        GenderIdentityCode.Other ||
        form.values[codesPatientValueName]?.value === PronounCode.Other) && (
        <TextInput
          placeholder={`${label} other`}
          {...form.getInputProps(`${codesPatientValueName}.text`)}
        />
      )}
    </EditUserFormBase>
  );
};

export default EditFormWithCode;
